import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useStore } from '../../../../app/data/root.store';
import { MetadataArrowRightIcon, MetadataTagsIcon } from '../../../assets/icons';
import { 
    SectionWrapper, 
    SectionHeader, 
    SectionHeaderLeft, 
    SectionHeaderRight, 
    SectionIcon, 
    SectionTitle, 
    TagCount, 
    ArrowIcon, 
    SectionContent, 
    EmptyState
} from './MetadataLabelsSection.styles';
import { PeriodTagCategory } from './PeriodTagCategory/PeriodTagCategory';
import { TagsTagCategory } from './TagsTagCategory/TagsTagCategory';
import { AttachedToItemsTagCategory } from './AttachedToItemsTagCategory/AttachedToItemsTagCategory';

export const MetadataLabelsSection = observer(({ file, onOpenTagEditorDrawer, onOpenLinkedItemDrawer }) => {
    const { vaultStore, metadataStore } = useStore();
    const [tagsOpen, setTagsOpen] = useState(false);

    useEffect(() => {
        if (vaultStore.userTags.length === 0) {
            vaultStore.fetchUserTags();
        }
        if (vaultStore.userPeriods.length === 0) {
            vaultStore.fetchUserPeriods();
        }
    }, [vaultStore]);

    const enhanceAttachedItems = (items) => {
        if (!items || !Array.isArray(items)) return [];
        if (!metadataStore.categoriesObj || !metadataStore.classesObj) return items;
        
        return items.map(item => {
            if (item.categoryClass) return item;
            
            const category = metadataStore.categoriesObj[item.categoryId];
            if (!category) return item;
            
            const assetClass = metadataStore.classesObj[category.classId];
            if (!assetClass) return item;
            
            return {
                ...item,
                categoryClass: {
                    id: assetClass.id,
                    title: assetClass.title || assetClass.name,
                    color: assetClass.color,
                    icon: assetClass.icon
                }
            };
        });
    };

    const getTagData = () => {
        const rawAttachedItems = file?.relatedItems || file?.attachedToItems || [];
        return {
            period: file?.periods?.length ? file.periods.map(p => p.period || p.label || p.key || p) : [],
            tags: file?.tags ? file.tags.map(t => t.tag || t.name || t.label || t) : [],
            attachedToItems: enhanceAttachedItems(rawAttachedItems)
        };
    };

    const tagData = getTagData();

    const getTotalTagCount = () => {
        const counts = Object.entries(tagData).reduce((total, [key, value]) => {
            if (key === 'attachedToItems') {
                return total + (Array.isArray(value) ? value.length : 0);
            }
            return total + (Array.isArray(value) ? value.length : 0);
        }, 0);
        return counts;
    };

    const handleTagsToggle = () => setTagsOpen(!tagsOpen);

    const updatePeriodTags = async (newPeriods) => {
        try {
            await vaultStore.updateSelectedVaultMetadata({
                periods: newPeriods
            });
        } catch (error) {
            console.error('Failed to update period tags:', error);
        }
    };

    const updateTags = async (newTags) => {
        try {
            await vaultStore.updateSelectedVaultMetadata({
                tags: newTags
            });
        } catch (error) {
            console.error('Failed to update tags:', error);
        }
    };

    const updateAttachedItems = async (newItems) => {
        try {
            const assetIds = newItems.map(item => item.id);
            await vaultStore.updateSelectedVaultMetadata({
                assetIds: assetIds
            });
        } catch (error) {
            console.error('Failed to update attached items:', error);
        }
    };

    const hasAnyTags = getTotalTagCount() > 0;

    const mainContent = (
        <>
            <PeriodTagCategory 
                periodTags={tagData.period} 
                availablePeriods={vaultStore.userPeriods}
                onUpdate={updatePeriodTags} 
            />
            <TagsTagCategory 
                tags={tagData.tags} 
                availableTags={vaultStore.userTags}
                onUpdate={updateTags} 
                onOpenTagEditorDrawer={onOpenTagEditorDrawer}
            />
            <AttachedToItemsTagCategory 
                attachedItems={tagData.attachedToItems}
                onUpdate={updateAttachedItems}
                fileName={file?.displayName || file?.originalName || 'Unknown File'} 
                onOpenLinkedItemDrawer={onOpenLinkedItemDrawer}
            />
        </>
    );

    return (
        <SectionWrapper>
            <SectionHeader isOpen={tagsOpen} onClick={handleTagsToggle}>
                <SectionHeaderLeft>
                    <SectionIcon><MetadataTagsIcon /></SectionIcon>
                    <SectionTitle>Labels</SectionTitle>
                </SectionHeaderLeft>
                <SectionHeaderRight>
                    {hasAnyTags && <TagCount>{getTotalTagCount()} label{getTotalTagCount() > 1 ? 's' : ''}</TagCount>}
                    <ArrowIcon isOpen={tagsOpen}><MetadataArrowRightIcon /></ArrowIcon>
                </SectionHeaderRight>
            </SectionHeader>
            {tagsOpen && (
                <SectionContent>
                    {!hasAnyTags ? (
                        <EmptyState>
                            {mainContent}
                        </EmptyState>
                    ) : (
                        <div style={{ gap: '8px', display: 'flex', flexDirection: 'column' }}>
                            {mainContent}
                        </div>
                    )}
                </SectionContent>
            )}
        </SectionWrapper>
    );
}); 